import React, { useEffect, useRef } from 'react';
import Lottie from 'lottie-react';
import { isNil, map } from 'lodash-es';
import { toast } from 'react-toastify';
import { FieldArray, Form, FormikProvider, useFormik } from 'formik';
import * as yup from 'yup';
import { v4 as uuid } from 'uuid';
import { Tooltip } from 'react-tooltip';
import { FaInfoCircle, FaTrashAlt } from 'react-icons/fa';
import { Accordion } from '@mantine/core';
import loader from '../../../assets/lotties/loader.json';
import './UpsertMenuItem.scss';
import { useCreateMenuItemMutation, useGetMenuItemByIdQuery, useUpdateMenuItemMutation } from '../../../store/menu/menuItem/menuItemApi';
import { ensureDataUrl, fileToDataUrl, stripDataUrl } from '../../../utils/imageHandlers';
import { protoEnumKeys } from '../../../utils/ProtoEnumKeys';
import { EItemOrderType, eItemOrderTypeToJSON } from '../../../proto/menu/item/enums/itemOrderType.enum';
import { ICreateMenuItemRequest, IGetMenuItemResponse } from '../../../proto/menu/item/menuItem';
import { yupEnum } from '../../../utils/yupValidators';
import { ICreateCustomizationGroupOptionRequest } from '../../../proto/menu/item/customizationGroupOption';
import { ICreateMenuItemCustomizationGroupRequest } from '../../../proto/menu/item/menuItemCustomizationGroup';

const newEmptyOption = (): ICreateCustomizationGroupOptionRequest => ({
	id: uuid(),
	name: '',
	priceAddOn: 0,
});

const newEmptyGroup = (): ICreateMenuItemCustomizationGroupRequest => ({
	id: uuid(),
	title: '',
	order: 0,
	min: 0,
	max: 0,
	options: [],
});

export const schema = yup.object({
	title: yup.string().required('שדה חובה'),
	description: yup.string().required('שדה חובה'),
	image: yup
		.string()
		.transform((v, orig) => (orig === '' ? null : v))
		.required('שדה חובה'),
	price: yup.number().min(0, 'Price must be at least 0').required('Price is required'),
	type: yupEnum({ valueEnum: EItemOrderType }).required('Type is required'),
	categoryId: yup.string().required('Category ID is required'),
	customizationGroups: yup.array().of(
		yup.object({
			title: yup.string().required('Customization group title is required'),
			order: yup.number().required('Order is required'),
			min: yup.number().min(0, 'Min must be at least 0').required('Min must be at least 0'),
			max: yup.number().min(0, 'Max must be at least 0').required('Max must be at least 0'),
			options: yup.array().of(
				yup.object({
					name: yup.string().required('Option name is required'),
					// optional but, if provided, must be >= 0
					priceAddOn: yup.number().min(0, 'Must be at least 0').optional(),
				})
			),
		})
	),
});

interface IUpsertMenuItemProps {
	menuId: string;
	categoryId: string;
	menuItemId?: string;
	onSubmit: (data: ICreateMenuItemRequest) => void;
	onCancel: () => void;
}

export const UpsertMenuItem = ({ menuId, categoryId, menuItemId, onSubmit, onCancel }: IUpsertMenuItemProps): JSX.Element => {
	const { data: menuItem, isLoading: isLoadingMenuItem } = useGetMenuItemByIdQuery(menuItemId, { skip: isNil(menuItemId) });

	const [createMenuItem] = useCreateMenuItemMutation();
	const [updateMenuItem] = useUpdateMenuItemMutation();

	const fileInputRef = useRef<HTMLInputElement | null>(null);

	const handleUpsertMenuItem = async (data: ICreateMenuItemRequest): Promise<IGetMenuItemResponse> => {
		// If your backend expects *plain* base64, strip the data URL prefix.
		const payloadImage = stripDataUrl(data.image ?? null);
		const body = { menuItemId, menuId, ...data, image: payloadImage };

		if (!isNil(menuItemId)) {
			return updateMenuItem(body).unwrap();
		}

		return createMenuItem(body).unwrap();
	};

	const onFormSubmit = (data: ICreateMenuItemRequest): void => {
		handleUpsertMenuItem(data)
			.then(() => {
				const status = isNil(menuItemId) ? 'נוצר' : 'עודכן';

				toast.success(`המוצר ${status} בהצלחה`);

				onSubmit(data);
			})
			.catch((err) => {
				console.error('Failed to upsert menu item:', err);
				toast.error('שגיאת עדכון');
			});
	};

	const formik = useFormik<ICreateMenuItemRequest>({
		initialValues: {
			title: menuItem?.title || '',
			description: menuItem?.description || '',
			type: menuItem?.type ?? EItemOrderType.REGULAR,
			// if server returns base64, show it as image:
			image: ensureDataUrl(menuItem?.image ?? null),
			price: menuItem?.price || 0,
			categoryId,
			customizationGroups: menuItem?.customizationGroups || [],
		},
		onSubmit: onFormSubmit,
		enableReinitialize: true,
		validationSchema: schema,
	});

	const { resetForm, values, setFieldValue, handleSubmit, getFieldProps, touched, errors, handleBlur, handleChange, getFieldMeta } = formik;

	useEffect(() => {
		console.log('errors', errors);
	}, [errors]);

	// reflect fetched menu item into the form
	useEffect(() => {
		if (!isNil(menuItem)) {
			resetForm({
				values: {
					title: menuItem?.title || '',
					description: menuItem?.description || '',
					// if server returns base64, show it as image:
					image: ensureDataUrl(menuItem?.image ?? null),
					price: menuItem?.price || 0,
					type: menuItem?.type || EItemOrderType.REGULAR,
					categoryId,
					customizationGroups: menuItem?.customizationGroups || [],
				},
			});

			// also clear the physical file input so the UI is synced
			if (fileInputRef.current) {
				fileInputRef.current.value = '';
			}
		}
	}, [categoryId, resetForm, menuItem]);

	const previewSrc = values.image ? values.image : null;

	const handleFileChange: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
		const file = e.target.files?.[0];

		if (!file) {
			return;
		}

		// Optional guards
		if (!file.type.startsWith('image/')) {
			toast.error('נא לבחור תמונת תמיכה בלבד');
			e.target.value = '';

			return;
		}

		// size guard (e.g. <= 2MB)
		if (file.size > 2 * 1024 * 1024) {
			toast.error('התמונה גדולה מדי (עד 2MB)');
			e.target.value = '';

			return;
		}

		try {
			const dataUrl = await fileToDataUrl(file);

			await setFieldValue('image', dataUrl, true);
		} catch (err) {
			toast.error('שגיאה בקריאת קובץ התמונה');
		}
	};

	const removeImage = async (): Promise<void> => {
		await setFieldValue('image', null, true);

		if (fileInputRef.current) {
			fileInputRef.current.value = '';
		}
	};

	// Helpers for enum options
	const orderTypeKeys = protoEnumKeys(EItemOrderType);
	const renderOrderTypeLabel = (type: EItemOrderType): string => {
		switch (type) {
			case EItemOrderType.REGULAR:
				return 'רגיל';
			case EItemOrderType.INFINITE_REFILL:
				return 'מילוי אין-סופי';
			case EItemOrderType.SINGLE_REFILL:
				return 'מילוי חד-פעמי';
			default:
				return eItemOrderTypeToJSON(type);
		}
	};

	const hasError = (fieldPath: string): boolean => {
		return getFieldMeta(fieldPath).touched && !isNil(getFieldMeta(fieldPath).error);
	};

	return (
		<div className="upsertMenuItemContainer">
			{isLoadingMenuItem ? (
				<Lottie animationData={loader} loop style={{ width: '10rem' }} />
			) : (
				<FormikProvider value={formik}>
					<Form onSubmit={handleSubmit}>
						{/* Title */}
						<div className="formGroup">
							<label>שם המוצר</label>
							<input {...getFieldProps('title')} />
							{touched.title && errors.title && <p>{errors.title}</p>}
						</div>

						{/* Description */}
						<div className="formGroup">
							<label>תיאור המוצר</label>
							<input {...getFieldProps('description')} />
							{touched.description && errors.description && <p>{errors.description}</p>}
						</div>

						{/* Price */}
						<div className="formGroup">
							<label>מחיר</label>
							<input
								type="number"
								step="0.01"
								min={0}
								name="price"
								value={values.price ?? ''}
								onChange={(e): void => {
									// parse as number like RHF's valueAsNumber
									const n = e.target.value === '' ? '' : Number(e.target.value);

									if (n === '') {
										setFieldValue('price', '');
									} else if (!Number.isNaN(n)) {
										setFieldValue('price', n, true);
									}
								}}
								onBlur={(e): void => {
									// normalize to 2 decimals on blur
									const n = Number(e.target.value);

									if (!Number.isNaN(n)) {
										setFieldValue('price', Number(n.toFixed(2)), true);
									}

									handleBlur(e);
								}}
							/>
							{touched.price && errors.price && <p>{errors.price}</p>}
						</div>

						{/* Type */}
						<div className="formGroup">
							<label>סוג</label>
							<select
								name="type"
								value={values.type ?? ''}
								onChange={(e): void => {
									setFieldValue('type', Number(e.target.value), true);
								}}
								onBlur={handleBlur}
							>
								{orderTypeKeys.map((typeKey) => (
									<option key={typeKey} value={EItemOrderType[typeKey]}>
										{renderOrderTypeLabel(EItemOrderType[typeKey])}
									</option>
								))}
							</select>
							{touched.type && errors.type && <p>{`${errors.type}`}</p>}
						</div>

						{/* Category (hidden field for submit */}
						<input type="hidden" name="categoryId" value={values.categoryId ?? ''} onChange={handleChange} />

						{/* IMAGE UPLOAD */}
						<div className="formGroup">
							<label>תמונה</label>
							<input
								ref={fileInputRef}
								type="file"
								accept="image/*"
								onChange={(e): void => {
									handleFileChange(e);
								}}
							/>
							{previewSrc && (
								<div className="imagePreview">
									<img
										src={ensureDataUrl(previewSrc) as string}
										alt="Preview"
										style={{ maxWidth: '220px', borderRadius: 8, display: 'block', marginTop: 8 }}
									/>
									<div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
										<button type="button" onClick={(): void => fileInputRef.current?.click()}>
											החלף תמונה
										</button>
										<button
											type="button"
											onClick={async (): Promise<void> => {
												await removeImage();
												await setFieldValue('image', '', true);
											}}
										>
											הסר תמונה
										</button>
									</div>
								</div>
							)}
							{/* Hidden image field managed by Formik */}
							<input type="hidden" name="image" value={values.image ?? ''} onChange={handleChange} />
							{touched.image && errors.image && <p>{errors.image}</p>}
						</div>

						{/* --- Customization Groups --- */}
						<div className="seperatedSubGroup">
							<div className="groupHeader">
								<label>תת בחירות</label>
								<FaInfoCircle id="groupd-explanation" />
								<Tooltip anchorSelect="#groupd-explanation" style={{ maxWidth: '200px' }}>
									לדוגמא עבור שניצלונים ובירה זו תהיה תת קטגוריית הבחירה של בירה
								</Tooltip>
							</div>

							<div className="groupContent">
								<FieldArray name="customizationGroups">
									{({ push, remove }): JSX.Element => (
										<>
											{values.customizationGroups?.length ? (
												<Accordion>
													{map(values.customizationGroups, (group: ICreateMenuItemCustomizationGroupRequest, gIdx: number) => (
														<Accordion.Item key={group.id} value={group.id}>
															<Accordion.Control>
																<div className="groupAccordionHeader">
																	<span> {group.title?.length ? group.title : `קבוצה חדשה`}</span>
																	<FaTrashAlt className="removeLine" onClick={(): void => remove(gIdx)} />
																</div>
															</Accordion.Control>
															<Accordion.Panel>
																<div key={group.id} className="customGroupCard">
																	{/* Group fields */}
																	<div className="customGroupGrid">
																		<div className="formGroup">
																			<label>כותרת</label>
																			<input {...getFieldProps(`customizationGroups.${gIdx}.title`)} />
																			{hasError(`customizationGroups.${gIdx}.title`) && (
																				<p>{getFieldMeta(`customizationGroups.${gIdx}.title`).error}</p>
																			)}
																		</div>

																		<div className="formGroup">
																			<label>סדר הצגה</label>
																			<input
																				type="number"
																				name={`customizationGroups.${gIdx}.order`}
																				value={group.order ?? ''}
																				onChange={(e): void => {
																					const n = e.target.value === '' ? '' : Number(e.target.value);

																					if (n === '') {
																						setFieldValue(`customizationGroups.${gIdx}.order`, '');
																					} else if (!Number.isNaN(n)) {
																						setFieldValue(`customizationGroups.${gIdx}.order`, n, true);
																					}
																				}}
																				onBlur={handleBlur}
																			/>
																			{hasError(`customizationGroups.${gIdx}.order`) && (
																				<p>{getFieldMeta(`customizationGroups.${gIdx}.order`).error}</p>
																			)}
																		</div>

																		<div className="formGroup">
																			<label>מינימום</label>
																			<input
																				type="number"
																				name={`customizationGroups.${gIdx}.min`}
																				value={group.min ?? ''}
																				onChange={(e): void => {
																					const n = e.target.value === '' ? '' : Number(e.target.value);

																					if (n === '') {
																						setFieldValue(`customizationGroups.${gIdx}.min`, '');
																					} else if (!Number.isNaN(n)) {
																						setFieldValue(`customizationGroups.${gIdx}.min`, n, true);
																					}
																				}}
																				onBlur={handleBlur}
																			/>
																			{hasError(`customizationGroups.${gIdx}.min`) && (
																				<p>{getFieldMeta(`customizationGroups.${gIdx}.min`).error}</p>
																			)}
																		</div>

																		<div className="formGroup">
																			<label>מקסימום</label>
																			<input
																				type="number"
																				name={`customizationGroups.${gIdx}.max`}
																				value={group.max ?? ''}
																				onChange={(e): void => {
																					const n = e.target.value === '' ? '' : Number(e.target.value);

																					if (n === '') {
																						setFieldValue(`customizationGroups.${gIdx}.max`, '');
																					} else if (!Number.isNaN(n)) {
																						setFieldValue(`customizationGroups.${gIdx}.max`, n, true);
																					}
																				}}
																				onBlur={handleBlur}
																			/>
																			{hasError(`customizationGroups.${gIdx}.max`) && (
																				<p>{getFieldMeta(`customizationGroups.${gIdx}.max`).error}</p>
																			)}
																		</div>
																	</div>

																	<div className="seperatedSubGroup">
																		<div className="groupHeader">
																			<label>אופציות</label>
																			<FaInfoCircle id="option-explanation" />
																			<Tooltip anchorSelect="#option-explanation" style={{ maxWidth: '200px' }}>
																				לדוגמא עבור שניצלונים ובירה זו תהיה רשימת האפשרויות של הבחירה של בירה
																			</Tooltip>
																		</div>
																		<div className="groupContent">
																			{/* Options array inside the group */}
																			<FieldArray name={`customizationGroups.${gIdx}.options`}>
																				{({ push: pushOption, remove: removeOption }): JSX.Element => (
																					<div className="optionsBlock">
																						{group.options?.length ? (
																							<Accordion>
																								{map(
																									group.options,
																									(
																										opt: ICreateCustomizationGroupOptionRequest,
																										oIdx: number
																									) => (
																										<Accordion.Item key={opt.id} value={opt.id}>
																											<Accordion.Control>
																												<div className="groupAccordionHeader">
																													<span>
																														{opt.name?.length
																															? opt.name
																															: `אופציה חדשה`}
																													</span>
																													<FaTrashAlt
																														className="removeLine"
																														onClick={(): void => removeOption(oIdx)}
																													/>
																												</div>
																											</Accordion.Control>
																											<Accordion.Panel>
																												<div key={opt.id} className="optionRow">
																													<div className="optionFields">
																														<div className="formGroup">
																															<label>שם אופציה</label>
																															<input
																																{...getFieldProps(
																																	`customizationGroups.${gIdx}.options.${oIdx}.name`
																																)}
																															/>
																															{hasError(
																																`customizationGroups.${gIdx}.options.${oIdx}.name`
																															) && (
																																<p>
																																	{
																																		getFieldMeta(
																																			`customizationGroups.${gIdx}.options.${oIdx}.name`
																																		).error
																																	}
																																</p>
																															)}
																														</div>

																														<div className="formGroup">
																															<label>תוספת מחיר</label>
																															<input
																																type="number"
																																step="0.01"
																																min={0}
																																name={`customizationGroups.${gIdx}.options.${oIdx}.priceAddOn`}
																																value={opt.priceAddOn ?? ''}
																																onChange={(e): void => {
																																	const n =
																																		e.target.value === ''
																																			? ''
																																			: Number(
																																					e.target
																																						.value
																																			  );

																																	if (n === '') {
																																		setFieldValue(
																																			`customizationGroups.${gIdx}.options.${oIdx}.priceAddOn`,
																																			''
																																		);
																																	} else if (
																																		!Number.isNaN(n)
																																	) {
																																		setFieldValue(
																																			`customizationGroups.${gIdx}.options.${oIdx}.priceAddOn`,
																																			n,
																																			true
																																		);
																																	}
																																}}
																																onBlur={(e): void => {
																																	const n = Number(
																																		e.target.value
																																	);

																																	if (!Number.isNaN(n)) {
																																		setFieldValue(
																																			`customizationGroups.${gIdx}.options.${oIdx}.priceAddOn`,
																																			Number(
																																				n.toFixed(2)
																																			),
																																			true
																																		);
																																	}

																																	handleBlur(e);
																																}}
																															/>
																															{hasError(
																																`customizationGroups.${gIdx}.options.${oIdx}.priceAddOn`
																															) && (
																																<p>
																																	{
																																		getFieldMeta(
																																			`customizationGroups.${gIdx}.options.${oIdx}.priceAddOn`
																																		).error
																																	}
																																</p>
																															)}
																														</div>
																													</div>
																												</div>
																											</Accordion.Panel>
																										</Accordion.Item>
																									)
																								)}
																							</Accordion>
																						) : (
																							<p style={{ opacity: 0.7 }}>אין אופציות בקבוצה זו.</p>
																						)}

																						<button
																							type="button"
																							onClick={(): void => pushOption(newEmptyOption())}
																						>
																							הוסף אופציה
																						</button>
																					</div>
																				)}
																			</FieldArray>
																		</div>
																	</div>
																</div>
															</Accordion.Panel>
														</Accordion.Item>
													))}
												</Accordion>
											) : (
												<p style={{ opacity: 0.7 }}>אין קבוצות עדיין.</p>
											)}

											<div style={{ marginTop: 8 }}>
												<button type="button" onClick={(): void => push(newEmptyGroup())}>
													הוסף קבוצה
												</button>
											</div>
										</>
									)}
								</FieldArray>
							</div>
						</div>

						<button type="submit" className="formHandlerButton saveForm">
							שמור
						</button>
						<button type="button" className="formHandlerButton cancelForm" onClick={onCancel}>
							בטל
						</button>
					</Form>
				</FormikProvider>
			)}
		</div>
	);
};
